// frontend/src/pages/ExitMemberWizard.jsx
// 4-step wizard to exit (archive) a member from Hope SACCO.
//
// Step 1 — Loan Check:    block if any active loans exist
// Step 2 — Shares Refund: confirm refund amount (shares − Ksh 5,000)
// Step 3 — Share Capital: transfer Ksh 5,000 to another member OR retain in SACCO
// Step 4 — Confirm & Exit

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import globalApi from '../utils/globalFetchWrapper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const fmt = (v) => Number(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// ── Authenticated fetch helper (now uses globalApi) ──────────────────────────
async function authedFetch(url, options = {}) {
  try {
    // Extract endpoint from full URL
    const endpoint = url.replace(API_URL, '');
    
    // Determine HTTP method
    const method = (options.method || 'GET').toUpperCase();
    
    // Use globalApi which handles token refresh, 401, etc.
    let response;
    if (method === 'GET') {
      response = await globalApi.get(endpoint);
    } else if (method === 'POST') {
      response = await globalApi.post(endpoint, options.body ? JSON.parse(options.body) : {});
    } else if (method === 'PUT') {
      response = await globalApi.put(endpoint, options.body ? JSON.parse(options.body) : {});
    } else if (method === 'DELETE') {
      response = await globalApi.delete(endpoint);
    } else {
      response = await globalApi.get(endpoint);
    }
    
    // Handle 429 (rate limit)
    if (response.status === 429) {
      throw new Error('Too many requests - please wait before trying again');
    }
    
    // Return JSON response
    return response.json();
  } catch (error) {
    if (error.message === 'Session expired') {
      throw error;
    }
    throw error;
  }
}

// ── Searchable member picker (portal — floats above all overflow:hidden) ─────
function MemberSearch({ members, value, onChange, excludeMemberId }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);

  const eligible = members.filter(m => m.memberId !== excludeMemberId && m.saccoInfo?.status !== 'Left');
  const selected = eligible.find(m => m.memberId === value);
  const displayLabel = selected
    ? `${selected.memberId} — ${selected.personalInfo?.fullName || selected.fullName || ''}`
    : '';
  const inputValue = open ? query : displayLabel;

  const filtered = query.trim()
    ? eligible.filter(m => {
        const q = query.toLowerCase();
        const name = (m.personalInfo?.fullName || m.fullName || '').toLowerCase();
        return m.memberId?.toLowerCase().includes(q) || name.includes(q);
      })
    : eligible;

  useEffect(() => {
    if (open && inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setPos({ top: r.bottom, left: r.left, width: r.width });
    }
  }, [open, query]);

  useEffect(() => {
    function onDown(e) {
      const portal = document.getElementById('ew-member-portal');
      if (inputRef.current && !inputRef.current.contains(e.target) &&
          (!portal || !portal.contains(e.target))) {
        setOpen(false); setQuery('');
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        placeholder="Search by name or ID…"
        value={inputValue}
        onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        style={inputStyle}
      />
      {open && filtered.length > 0 && ReactDOM.createPortal(
        <div id="ew-member-portal" style={{
          position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
          background: '#fff', border: '1px solid #ddd', borderRadius: 6,
          maxHeight: 260, overflowY: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', zIndex: 999999,
        }}>
          {filtered.slice(0, 50).map(m => (
            <div key={m.memberId}
              onMouseDown={e => { e.preventDefault(); onChange(m.memberId); setQuery(''); setOpen(false); }}
              style={{ padding: '10px 14px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #f5f5f5', background: '#fff' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f0ff'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <strong>{m.memberId}</strong> — {m.personalInfo?.fullName || m.fullName || ''}
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '10px 14px', border: '1px solid #ddd',
  borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const infoBox = (color) => ({
  background: color === 'red' ? '#fef2f2' : color === 'green' ? '#f0fdf4' : color === 'yellow' ? '#fffbeb' : '#eff6ff',
  border: `1px solid ${color === 'red' ? '#fca5a5' : color === 'green' ? '#86efac' : color === 'yellow' ? '#fcd34d' : '#93c5fd'}`,
  borderRadius: 8, padding: '14px 18px', marginBottom: 16,
});
const fieldRow = { marginBottom: 18 };

// ── Step indicator ────────────────────────────────────────────────────────────
function StepIndicator({ current, steps }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32, gap: 0 }}>
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15,
                background: done ? '#10b981' : active ? '#4b3ff6' : '#e5e7eb',
                color: done || active ? '#fff' : '#6b7280',
                border: active ? '2px solid #4b3ff6' : 'none',
                transition: 'all 0.2s',
              }}>
                {done ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: 11, marginTop: 5, color: active ? '#4b3ff6' : done ? '#10b981' : '#9ca3af', fontWeight: active ? 700 : 400, textAlign: 'center' }}>
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 3, background: done ? '#10b981' : '#e5e7eb', marginBottom: 24, transition: 'background 0.3s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Main wizard component ─────────────────────────────────────────────────────
export default function ExitMemberWizard() {
  const { id: memberId } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [preflight, setPreflight] = useState(null);   // preflight response data
  const [allMembers, setAllMembers] = useState([]);    // for recipient picker

  // Form state
  const [exitYear, setExitYear] = useState('');
  const [exitMonth, setExitMonth] = useState('');
  const exitDate = exitYear && exitMonth ? `${exitYear}-${exitMonth}` : '';
  const [leftReason, setLeftReason] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [transferType, setTransferType] = useState('transfer'); // 'transfer' | 'retain'
  const [recipientMemberId, setRecipientMemberId] = useState('');
  const [result, setResult] = useState(null);
  // Shares-to-loan offsets: { normal: '26000', school: '', emergency: '' } — string inputs from the admin
  const [loanOffsetInputs, setLoanOffsetInputs] = useState({ normal: '', school: '', emergency: '' });

  // If the member has no share capital (Ksh 0 shares), skip the Share Capital step
  const hasShareCapital = preflight ? preflight.shareCapitalAmount > 0 : true;
  const STEPS = hasShareCapital
    ? ['Loan Check', 'Shares Refund', 'Share Capital', 'Confirm & Exit']
    : ['Loan Check', 'Shares Refund', 'Confirm & Exit'];
  // Internal step indices — step 2 is Share Capital only when hasShareCapital
  const CONFIRM_STEP = hasShareCapital ? 3 : 2;

  // ── Load member list once on mount (separate from preflight, doesn't depend on exitDate) ──
  useEffect(() => {
    (async () => {
      try {
        const membersRes = await authedFetch(`${API_URL}/members?limit=500`);
        if (membersRes.success) {
          setAllMembers(membersRes.data?.members || []);
        }
      } catch (e) {
        // non-fatal — recipient picker just won't have data
      }
    })();
  }, []);

  // ── Load/reload preflight whenever exitDate changes ─────────────────────
  // This ensures loan balances and shares are calculated AS OF the chosen exit month,
  // not always "today". Until an exit month is chosen, preflight uses today's date
  // (so the page isn't blank on first load), then re-fetches with the real figures
  // the moment the admin picks a month.
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const qs = exitDate ? `?exitDate=${exitDate}` : '';
        const preRes = await authedFetch(`${API_URL}/members/${memberId}/exit/preflight${qs}`);
        if (!preRes.success) {
          setError(preRes.message || 'Could not load member exit information');
        } else {
          setPreflight(preRes.data);
          // Reset loan offset inputs — figures from a previous exitDate selection
          // are no longer valid against the newly loaded balances.
          setLoanOffsetInputs({ normal: '', school: '', emergency: '' });
        }
      } catch (e) {
        setError('Network error: ' + e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [memberId, exitDate]);

  // ── Loan offset calculations (client-side preview only — server re-validates on submit) ──
  const loanOffsetTotal = Object.values(loanOffsetInputs).reduce((s, v) => s + (Number(v) || 0), 0);
  const availableForOffset = preflight?.availableSharesForOffset || 0;
  const loanOffsetExceedsAvailable = loanOffsetTotal > availableForOffset;

  // For each currently-blocking loan, does the entered offset amount fully cover what's
  // still projected to remain after this month's scheduled payment (NOT the full opening balance)?
  const blockingLoansCoveredByOffsets = (preflight?.activeLoans || []).every(l => {
    const entered = Number(loanOffsetInputs[l.loanType]) || 0;
    return entered >= l.remainingBalance;
  });

  const willClearAllLoansWithOffsets =
    (preflight?.activeLoans?.length > 0) &&
    !loanOffsetExceedsAvailable &&
    blockingLoansCoveredByOffsets;

  // ── Navigation helpers ──────────────────────────────────────────────────
  const canGoNext = () => {
    if (step === 0) {
      const loanOk = preflight?.canExit === true || willClearAllLoansWithOffsets;
      return loanOk && !!exitDate;
    }
    if (step === 1) return true; // refund notes optional
    if (hasShareCapital && step === 2) {
      if (transferType === 'transfer') return !!recipientMemberId;
      return true; // retain
    }
    return false;
  };

  const handleNext = () => { if (canGoNext()) setStep(s => s + 1); };
  const handleBack = () => setStep(s => s - 1);

  // ── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!window.confirm(
      `⚠️ You are about to permanently exit ${preflight?.memberName} from Hope SACCO.\n\nThis action cannot be undone. Their login will be disabled.\n\nProceed?`
    )) return;

    setSubmitting(true);
    setError('');
    try {
      const loanOffsets = Object.entries(loanOffsetInputs)
        .filter(([, v]) => Number(v) > 0)
        .map(([loanType, v]) => ({ loanType, amount: Number(v) }));

      const res = await authedFetch(`${API_URL}/members/${memberId}/exit`, {
        method: 'POST',
        body: JSON.stringify({
          leftReason,
          refundNotes,
          exitDate,
          shareCapitalTransfer: {
            type: transferType,
            recipientMemberId: transferType === 'transfer' ? recipientMemberId : '',
          },
          loanOffsets,
        }),
      });
      if (res.success) {
        setResult(res.data);
        setStep(4); // success screen
      } else {
        setError(res.message || 'Exit failed. Please try again.');
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived display values ──────────────────────────────────────────────
  const recipientMember = allMembers.find(m => m.memberId === recipientMemberId);
  const recipientName = recipientMember?.personalInfo?.fullName || recipientMember?.fullName || '';

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <div style={{ padding: '24px', maxWidth: 720, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <button onClick={() => navigate('/admin/members')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4b3ff6', fontSize: 14, padding: 0 }}>
              ← Members
            </button>
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: '#111' }}>Exit Member from SACCO</h1>
          <p style={{ color: '#6b7280', marginTop: 4, fontSize: 14 }}>
            Complete all steps to permanently archive this member.
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
            Loading member information…
          </div>
        )}

        {/* Hard error (e.g. member not found) */}
        {!loading && error && !preflight && (
          <div style={infoBox('red')}>
            <strong>❌ Error:</strong> {error}
            <br />
            <button onClick={() => navigate('/admin/members')}
              style={{ marginTop: 12, padding: '8px 16px', background: '#4b3ff6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
              Back to Members
            </button>
          </div>
        )}

        {/* Success screen */}
        {step === 4 && result && (
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h2 style={{ color: '#166534', marginBottom: 8 }}>Member Exited Successfully</h2>
            <p style={{ color: '#374151', marginBottom: 20 }}>
              <strong>{preflight?.memberName}</strong> ({memberId}) has been archived. Their login has been disabled.
            </p>
            <div style={{ background: '#fff', borderRadius: 8, padding: 20, textAlign: 'left', marginBottom: 24, fontSize: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px' }}>
                <div><span style={{ color: '#6b7280' }}>Total shares at exit</span><br /><strong>Ksh {fmt(result.totalShares)}</strong></div>
                <div><span style={{ color: '#6b7280' }}>Amount refunded (cash)</span><br /><strong>Ksh {fmt(result.refundAmount)}</strong></div>
                {result.sharesAppliedToLoans > 0 && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <span style={{ color: '#6b7280' }}>Settled using shares</span><br />
                    <strong>Ksh {fmt(result.sharesAppliedToLoans)}{result.loanOffsets?.length
                      ? ` — ${result.loanOffsets.map(o => `${o.loanType}: Ksh ${fmt(o.applied)}`).join(', ')}`
                      : ''}</strong>
                  </div>
                )}
                <div><span style={{ color: '#6b7280' }}>Share capital (Ksh 5,000)</span><br />
                  <strong>{result.shareCapitalTransferType === 'transfer'
                    ? `Transferred to ${recipientName || result.shareCapitalRecipient}`
                    : 'Retained in SACCO'}</strong>
                </div>
                <div><span style={{ color: '#6b7280' }}>Login disabled</span><br /><strong>{result.authDisabled ? 'Yes' : 'Account not found (may not have had login)'}</strong></div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => navigate('/admin/members')}
                style={{ padding: '10px 24px', background: '#4b3ff6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                Back to Members
              </button>
              <button onClick={() => navigate('/admin/members?tab=left')}
                style={{ padding: '10px 24px', background: '#fff', color: '#4b3ff6', border: '1px solid #4b3ff6', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                View Left Members
              </button>
            </div>
          </div>
        )}

        {/* Wizard steps */}
        {!loading && preflight && step < 4 && (
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 32 }}>
            <StepIndicator current={step} steps={STEPS} />

            {/* Member identity banner */}
            <div style={{ background: '#f8f8ff', border: '1px solid #e0e0ff', borderRadius: 8, padding: '12px 16px', marginBottom: 24, display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#4b3ff6', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, flexShrink: 0 }}>
                {(preflight.memberName || 'M')[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{preflight.memberName}</div>
                <div style={{ color: '#6b7280', fontSize: 13 }}>{memberId} · Current status: {preflight.currentStatus}</div>
              </div>
            </div>

            {/* Inline error (non-fatal) */}
            {error && (
              <div style={infoBox('red')} role="alert">⚠️ {error}</div>
            )}

            {/* ── STEP 0: Loan Check ──────────────────────────────────── */}
            {step === 0 && (
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Step 1 — Loan Check</h2>
                <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
                  A member can only exit if all their loans have been fully settled.
                </p>

                {preflight.canExit ? (
                  <div style={infoBox('green')}>
                    <strong>✅ Clear to proceed.</strong> This member has no active loans.
                  </div>
                ) : (
                  <div style={infoBox('red')}>
                    <strong>❌ Cannot exit — {preflight.activeLoans.length} active loan(s) outstanding.</strong>
                    <p style={{ margin: '8px 0 4px', fontSize: 13, color: '#6b7280' }}>All loans below must be settled before this member can exit:</p>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
                      <thead>
                        <tr style={{ background: '#fee2e2' }}>
                          <th style={{ padding: '6px 10px', textAlign: 'left' }}>Loan Type</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' }}>Balance (start of {exitDate || 'this month'})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preflight.activeLoans.map(l => (
                          <tr key={l.loanType} style={{ borderBottom: '1px solid #fca5a5' }}>
                            <td style={{ padding: '6px 10px', textTransform: 'capitalize', fontWeight: 600 }}>{l.loanType}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right' }}>Ksh {fmt(l.currentBalance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {availableForOffset > 0 && (
                      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #fca5a5' }}>
                        <strong style={{ fontSize: 13 }}>💰 Settle a loan using this member's shares instead</strong>
                        <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 10px' }}>
                          Ksh {fmt(availableForOffset)} in shares is available (the Ksh 5,000 share capital floor is never used for this).
                          The amount below is what's still projected to remain after this month's scheduled repayment —
                          enter how much of that to settle with shares instead of requiring a cash payment.
                        </p>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <tbody>
                            {preflight.activeLoans.map(l => (
                              <tr key={l.loanType}>
                                <td style={{ padding: '4px 8px', textTransform: 'capitalize', fontWeight: 600, width: '18%' }}>{l.loanType}</td>
                                <td style={{ padding: '4px 8px', width: '34%' }}>
                                  <input
                                    type="number"
                                    min={0}
                                    max={Math.min(l.remainingBalance, availableForOffset)}
                                    value={loanOffsetInputs[l.loanType]}
                                    onChange={e => setLoanOffsetInputs(prev => ({ ...prev, [l.loanType]: e.target.value }))}
                                    placeholder={`up to Ksh ${fmt(Math.min(l.remainingBalance, availableForOffset))}`}
                                    style={{ ...inputStyle, padding: '8px 10px', minWidth: 160, width: '100%', boxSizing: 'border-box' }}
                                  />
                                </td>
                                <td style={{ padding: '4px 8px', fontSize: 12, color: '#6b7280', whiteSpace: 'normal' }}>
                                  of Ksh {fmt(l.remainingBalance)} still remaining
                                  {l.remainingBalance < l.currentBalance && (
                                    <span style={{ color: '#9ca3af' }}> (Ksh {fmt(l.currentBalance - l.remainingBalance)} already covered by this month's scheduled payment)</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {loanOffsetTotal > 0 && (
                          <div style={{
                            marginTop: 10, fontSize: 12, padding: '8px 10px', borderRadius: 6,
                            background: loanOffsetExceedsAvailable ? '#fef2f2' : willClearAllLoansWithOffsets ? '#f0fdf4' : '#fffbeb',
                            color: loanOffsetExceedsAvailable ? '#991b1b' : willClearAllLoansWithOffsets ? '#166534' : '#92400e',
                            border: `1px solid ${loanOffsetExceedsAvailable ? '#fca5a5' : willClearAllLoansWithOffsets ? '#86efac' : '#fcd34d'}`,
                          }}>
                            {loanOffsetExceedsAvailable
                              ? `⚠️ Total entered (Ksh ${fmt(loanOffsetTotal)}) exceeds available shares (Ksh ${fmt(availableForOffset)}).`
                              : willClearAllLoansWithOffsets
                                ? `✅ This fully settles all outstanding loans using shares — Next will unlock. The remaining Ksh ${fmt(availableForOffset - loanOffsetTotal)} in shares will still be refunded in cash.`
                                : `ℹ️ Ksh ${fmt(loanOffsetTotal)} entered, but at least one loan still won't be fully covered — exit will remain blocked until every loan above is either paid in cash or fully covered here.`}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Advisory: loans that clear this month — shown in blue, not blocking */}
                {preflight.clearedThisMonth?.length > 0 && (
                  <div style={{ ...infoBox('blue'), marginTop: 10 }}>
                    <strong>ℹ️ Cleared by {exitDate || 'this month'}:</strong>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
                      <thead>
                        <tr style={{ background: '#dbeafe' }}>
                          <th style={{ padding: '6px 10px', textAlign: 'left' }}>Loan Type</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' }}>Was Outstanding (month before)</th>
                          <th style={{ padding: '6px 10px', textAlign: 'left' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preflight.clearedThisMonth.map(l => (
                          <tr key={l.loanType} style={{ borderBottom: '1px solid #bfdbfe' }}>
                            <td style={{ padding: '6px 10px', textTransform: 'capitalize', fontWeight: 600 }}>{l.loanType}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right', color: '#6b7280' }}>Ksh {fmt(l.prevBalance)}</td>
                            <td style={{ padding: '6px 10px', color: '#1d4ed8', fontWeight: 600 }}>✓ Paid by {exitDate || 'this month'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ fontSize: 12, color: '#1e40af', marginTop: 8 }}>
                      These loans are fully settled by end of {exitDate || 'this month'} and do not block exit.
                    </div>
                  </div>
                )}

                <div style={fieldRow}>
                  <label style={labelStyle}>Exit Month <span style={{ fontWeight: 400, color: '#9ca3af' }}>(when did the member leave?)</span></label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select
                      value={exitYear}
                      onChange={e => setExitYear(e.target.value)}
                      style={{ padding: '10px 12px', border: `1px solid ${exitYear ? '#ddd' : '#f59e0b'}`, borderRadius: 6, fontSize: 14, minWidth: 100 }}
                    >
                      <option value=''>Year</option>
                      {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => (
                        <option key={y} value={String(y)}>{y}</option>
                      ))}
                    </select>
                    <select
                      value={exitMonth}
                      onChange={e => setExitMonth(e.target.value)}
                      style={{ padding: '10px 12px', border: `1px solid ${exitMonth ? '#ddd' : '#f59e0b'}`, borderRadius: 6, fontSize: 14, minWidth: 130 }}
                    >
                      <option value=''>Month</option>
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                        <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ fontSize: 12, color: exitDate ? '#6b7280' : '#ef4444', marginTop: 6 }}>
                    {exitDate
                      ? `Exit date: ${exitDate} — loan balances and shares above are calculated as of this month.`
                      : '⚠️ Select exit Month'}
                  </div>
                </div>

                <div style={fieldRow}>
                  <label style={labelStyle}>Reason for Leaving <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                  <textarea
                    value={leftReason}
                    onChange={e => setLeftReason(e.target.value)}
                    placeholder="e.g. Relocated, retired, personal reasons…"
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </div>
              </div>
            )}

            {/* ── STEP 1: Shares Refund ──────────────────────────────── */}
            {step === 1 && (
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Step 2 — Shares Refund</h2>
                <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
                  The member's shares minus the Ksh 5,000 share capital
                  {loanOffsetTotal > 0 ? ', minus any amount applied to loans,' : ''} will be refunded.
                  Record any notes about how the payment was made.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: loanOffsetTotal > 0 ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                  {[
                    { label: 'Total Shares', value: `Ksh ${fmt(preflight.totalShares)}`, color: '#4b3ff6' },
                    { label: 'Share Capital Retained', value: `Ksh ${fmt(preflight.shareCapitalAmount)}`, color: '#f59e0b' },
                    ...(loanOffsetTotal > 0 ? [{ label: 'Used to Settle Loan(s)', value: `Ksh ${fmt(loanOffsetTotal)}`, color: '#7c3aed' }] : []),
                    { label: 'Amount to Refund (cash)', value: `Ksh ${fmt(Math.max(0, preflight.refundAmount - loanOffsetTotal))}`, color: '#10b981' },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ background: '#f9f9ff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
                    </div>
                  ))}
                </div>

                {loanOffsetTotal > 0 && (
                  <div style={{ ...infoBox('blue'), marginBottom: 16 }}>
                    <strong style={{ fontSize: 13 }}>📌 Loan(s) settled using shares:</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 13 }}>
                      {Object.entries(loanOffsetInputs).filter(([, v]) => Number(v) > 0).map(([type, amt]) => (
                        <li key={type} style={{ textTransform: 'capitalize' }}>{type}: Ksh {fmt(Number(amt))}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {preflight.totalShares === 0 ? (
                  <div style={infoBox('yellow')}>
                    ℹ️ <strong>Note:</strong> This member has <strong>Ksh 0</strong> in shares as at the closing of last month. There is nothing to refund and no share capital to transfer. You may proceed directly to exit.
                  </div>
                ) : (
                <div style={infoBox('yellow')}>
                  ℹ️ <strong>Important:</strong> The refund of <strong>Ksh {fmt(preflight.refundAmount)}</strong> must be paid to the member outside the system (cash/bank transfer). Record details here for the audit trail.
                </div>
                )}

                <div style={fieldRow}>
                  <label style={labelStyle}>Refund Payment Notes</label>
                  <textarea
                    value={refundNotes}
                    onChange={e => setRefundNotes(e.target.value)}
                    placeholder="e.g. Cash paid to member on 01 Jan 2026. Receipt #1234."
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </div>
              </div>
            )}

            {/* ── STEP 2: Share Capital Transfer ────────────────────── */}
            {step === 2 && (
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Step 3 — Share Capital (Ksh {fmt(preflight.shareCapitalAmount)})</h2>
                <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
                  Each member must leave Ksh 5,000 as share capital in the SACCO. You can transfer this to another member now, or retain it in the SACCO until a buyer is found.
                </p>

                <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                  {[
                    { val: 'transfer', label: '🔄 Transfer to Another Member', desc: 'Immediately credits Ksh 5,000 to a member you choose.' },
                    { val: 'retain', label: '🏦 Retain in SACCO', desc: 'Keep it on record. No immediate transfer.' },
                  ].map(({ val, label, desc }) => (
                    <div key={val}
                      onClick={() => setTransferType(val)}
                      style={{
                        flex: 1, padding: '14px 16px', borderRadius: 8, cursor: 'pointer',
                        border: `2px solid ${transferType === val ? '#4b3ff6' : '#e5e7eb'}`,
                        background: transferType === val ? '#f0f0ff' : '#fff',
                      }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{desc}</div>
                    </div>
                  ))}
                </div>

                {transferType === 'transfer' && (
                  <div style={fieldRow}>
                    <label style={labelStyle}>Select Recipient Member</label>
                    <MemberSearch
                      members={allMembers}
                      value={recipientMemberId}
                      onChange={setRecipientMemberId}
                      excludeMemberId={memberId}
                    />
                    {recipientMemberId && recipientMember && (
                      <div style={{ ...infoBox('green'), marginTop: 10, fontSize: 13 }}>
                        ✅ Ksh {fmt(preflight.shareCapitalAmount)} will be added to <strong>{recipientName}</strong>'s ({recipientMemberId}) shares.
                      </div>
                    )}
                    {transferType === 'transfer' && !recipientMemberId && (
                      <div style={{ fontSize: 12, color: '#ef4444', marginTop: 6 }}>
                        Please select a recipient to continue.
                      </div>
                    )}
                  </div>
                )}

                {transferType === 'retain' && (
                  <div style={infoBox('yellow')}>
                    ℹ️ The share capital will be noted as retained in SACCO. You can transfer it later via Manage Shares on the recipient's account.
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 3/2: Summary & Confirm ──────────────────────── */}
            {step === CONFIRM_STEP && (
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Step 4 — Confirm &amp; Exit</h2>
                <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
                  Review the summary below. Once you click <strong>Exit Member</strong>, this cannot be undone.
                </p>

                <div style={{ background: '#f9f9ff', border: '1px solid #e0e0ff', borderRadius: 10, padding: 20, marginBottom: 20 }}>
                  <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#4b3ff6' }}>Exit Summary</h3>
                  <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
                    <tbody>
                      {[
                        ['Member', `${preflight.memberName} (${memberId})`],
                        ['Total shares at exit', `Ksh ${fmt(preflight.totalShares)}`],
                        ...(loanOffsetTotal > 0 ? [['Loan(s) settled using shares', Object.entries(loanOffsetInputs)
                          .filter(([, v]) => Number(v) > 0)
                          .map(([type, amt]) => `${type}: Ksh ${fmt(Number(amt))}`)
                          .join(', ')]] : []),
                        ['Refund to member (cash)', `Ksh ${fmt(Math.max(0, preflight.refundAmount - loanOffsetTotal))}`],
                        ['Refund notes', refundNotes || '(none)'],
                        ['Share capital', !hasShareCapital
                          ? 'None (member had no shares)'
                          : transferType === 'transfer'
                            ? `Ksh ${(preflight?.shareCapitalAmount || 0).toLocaleString()} → Transfer to ${recipientName} (${recipientMemberId})`
                            : `Ksh ${(preflight?.shareCapitalAmount || 0).toLocaleString()} — Retain in SACCO`],
                        ['Exit Month', exitDate],
                        ['Reason for leaving', leftReason || '(not specified)'],
                        ['Login', 'Will be disabled immediately'],
                      ].map(([label, value]) => (
                        <tr key={label} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '8px 0', color: '#6b7280', width: '45%' }}>{label}</td>
                          <td style={{ padding: '8px 0', fontWeight: 600 }}>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={infoBox('red')}>
                  ⚠️ <strong>This is permanent.</strong> {preflight.memberName} will be archived as "Left", their login will be disabled, and they will be excluded from all operational pages. This cannot be reversed through the UI.
                </div>
              </div>
            )}

            {/* ── Navigation buttons ───────────────────────────────── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28, paddingTop: 20, borderTop: '1px solid #f0f0f0' }}>
              <button
                onClick={step === 0 ? () => navigate('/admin/members') : handleBack}
                style={{ padding: '10px 22px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                {step === 0 ? '← Cancel' : '← Back'}
              </button>

              {step < CONFIRM_STEP ? (
                <button
                  onClick={handleNext}
                  disabled={!canGoNext()}
                  style={{
                    padding: '10px 26px', background: canGoNext() ? '#4b3ff6' : '#c7d2fe',
                    color: '#fff', border: 'none', borderRadius: 6,
                    cursor: canGoNext() ? 'pointer' : 'not-allowed', fontWeight: 600,
                  }}
                >
                  Next →
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    padding: '10px 28px', background: submitting ? '#9ca3af' : '#dc2626',
                    color: '#fff', border: 'none', borderRadius: 6,
                    cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 15,
                  }}
                >
                  {submitting ? 'Processing…' : '🚪 Exit Member'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
